import { Button, Card, Dropdown, notification, Pagination, Table } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { isArrayNonEmpty } from '../../util/ApiHelper'
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useGetBreakpoints } from '../../hooks/useGetBreakpoints';
import ListEmptyComponent from '../../components/ListEmptyComponent/ListEmptyComponent';
import { formatDate } from '../../util/helper';
import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, MoreOutlined } from '@ant-design/icons';
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { axiosInstance, BASE_API_CONFIG } from '../../util/Api';

const OfferProductsAdmin = () => {
  const { isMobile } = useGetBreakpoints();
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { loading } = useSelector(({ common }) => common);
  const [adminOfferProductList, setAdminOfferProductList] = useState({
  data: [],
  meta: {}
});

const getAdminOfferProducts = async (page = currentPage, limit = pageSize) => {
  try {
    dispatch(fetchStart());
    const response = await axiosInstance.get(
      `${BASE_API_CONFIG.baseURL}/admin-offer-products?page=${page}&limit=${limit}`
    );

    setAdminOfferProductList(response?.data);
  } catch (error) {
    notification.error({
      message: "Failed",
      description:
        error?.response?.data?.message || "Failed to fetch admin offers",
    });
  } finally {
    dispatch(fetchSuccess())
  }
};

useEffect(() => {
  getAdminOfferProducts(currentPage, pageSize);
}, [currentPage, pageSize]);

  const rows = useMemo(() => {
    if (!adminOfferProductList) return [];
    if (Array.isArray(adminOfferProductList.data)) return adminOfferProductList.data;
    if (Array.isArray(adminOfferProductList)) return adminOfferProductList;
    return [];
  }, [adminOfferProductList]);

  const totalCount = useMemo(() => {
    return adminOfferProductList?.meta?.total ?? adminOfferProductList?.count ?? rows.length;
  }, [adminOfferProductList, rows]);

  const navigateToDetails = (id, isEditMode = false) => {
    const path = isEditMode
      ? `offer-your-cargo-admin/add-edit/${id}`
      : `offer-your-cargo-admin/add-edit`;
    history.push(path);
  }

  const navigateToViewDetails = (id) => {
      const path = `offer-your-cargo-admin/view/${id}`;
      history.push(path);
  }

  const handleCreateAdminOfferCargo = () => {
    navigateToDetails();
  }

    const handleDelete = async (id) => {
        try{
          dispatch(fetchStart());
          await axiosInstance.delete(
            `${BASE_API_CONFIG.baseURL}/admin-offer-products/${id}`
          );
          notification.success({
            message: 'Success',
            description: 'Offer deleted successfully'
          });
          getAdminOfferProducts(currentPage,pageSize);
        }catch(error){
          notification.error({
            message: 'Error',
            description: error?.response?.data?.message || 'Failed to delete offer'
          })
        }finally{
          dispatch(fetchSuccess());
        }
      }
  
    const onShowSizeChange = useCallback(
      (current, pageSize) => {
        setCurrentPage(current);
        setPageSize(pageSize);
      },
      [dispatch]
    );
  
    const handleClone = (record) => {
        const { id, ...cloneData } = record;
        history.push({
          pathname: "offer-your-cargo-admin/add-edit",
          state: { cloneData }
        });
    }

  const moreActionsRenderer = useCallback(
    (record) => (
      <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          menu={{
            items: [
              {
                label: "View",
                key: "1",
                icon: <EyeOutlined />,
                onClick: () => {
                  navigateToViewDetails(record?.id);
                },
              },
              {
                label: "Clone",
                key: "4",
                icon: <CopyOutlined />,
                onClick: () => {
                  handleClone(record);
                },
              },
              {
                label: "Edit",
                key: "2",
                icon: <EditOutlined />,
                onClick: () => {
                  navigateToDetails(record.id, true);
                },
              },
              {
                label: "Delete",
                key: "3",
                icon: <DeleteOutlined />,
                onClick: () => {
                  handleDelete(record.id);
                },
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined />
        </Dropdown>
      </div>
    ),
    [navigateToDetails, navigateToViewDetails, handleClone]
  );

  const columns = useMemo(
    () => [
      {
        title: "Updated Date",
        dataIndex: "updatedAt",
        width: "25%",
        render: (updatedAt) => <div>{formatDate(updatedAt)}</div>,
      },
      {
        title: "Product Name",
        dataIndex: "name",
        width: "25%",
        render: (text) => (
            <div className={"highlighted-column"}>{text}</div>
        ),
      },
      {
        title: "FOB/FOR Price",
        dataIndex: "price",
        render: (price, record) => (
          <div>{`${record.currency} ${price} / ${record.uom || ""}`}</div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: moreActionsRenderer,
      },
    ],
    [moreActionsRenderer]
  );


  const renderContent = useCallback((data, count) => {
    return isMobile ? (
      <>
        <Card bodyStyle={{ padding: 0 }}>
          {data.map((record) => {
            return (
              <div
                className="item-detail-mobile gx-flex-row gx-pointer gx-border-bottom gx-align-items-center"
                onClick={() => navigateToViewDetails(record.id)}
              >
                <div className="item-details gx-flex-1 gx-flex-column">
                  <div className="item-posted-date">
                    Posted on {formatDate(record?.createdAt)}
                  </div>
                  <div className="item-primary-row highlighted-column gx-flex-row gx-justify-content-between">
                    <div className="item-primary-text">{record?.name}</div>
                  </div>
                  <div className="item-additional-content gx-flex-row gx-justify-content-between gx-align-items-center">
                    <div className="item-secondary-text">
                      <span style={{ color: "#555555", fontWeight: 600 }}>
                        FOB Price:{" "}
                      </span>
                      <span style={{ fontWeight: 500 }}>
                        {`${record.currency} ${record.price} / ${record.uom || ""
                          }`}
                      </span>
                    </div>
                    <div>{moreActionsRenderer(record)}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </Card>
        <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={count}
        onChange={onShowSizeChange}
        showSizeChanger
        style={{ marginTop: 16, textAlign: "right" }}
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
      />
      </>
    ) : (
      <Table
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        onRow={(record) => {
          return {
            onClick: () => {
              navigateToViewDetails(record?.id);
            },
          };
        }}
        className="gx-table-responsive my-product-table myexim-table"
        total={count}
        columns={columns}
        dataSource={data}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          position: ["bottomRight"],
          showSizeChanger: true,
          total: count,
          onChange: onShowSizeChange,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    );
  }, [isMobile, navigateToViewDetails, moreActionsRenderer, columns, currentPage, pageSize, onShowSizeChange])

  return (
    <Card
      bodyStyle={{ padding: "24px " }}
      style={{ width: "100%" }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className='myexim-table-title'>
            Offer Your Cargo
          </div>
          {
            isArrayNonEmpty(rows) && (
              <Button type="primary" onClick={handleCreateAdminOfferCargo}>
                {isMobile ? "+" : "+ Create New"}
              </Button>
            )
          }
        </div>
      }
    >
      <div>
        {
          isArrayNonEmpty(rows)
            ? renderContent(
              rows, totalCount
            )
            : !loading && (
              <ListEmptyComponent
                buttonText='Create New Offer'
                onButtonClick={
                  handleCreateAdminOfferCargo
                }
              />
            )
        }
      </div>
    </Card>
  )
}

export default OfferProductsAdmin
